import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate } from '../../lib/formatters.js';
import { SettlementPreviewTable } from './components/SettlementPreviewTable.js';

interface BatchDetail {
  id: string;
  status: string;
  settlementMethod: string;
  bankFormat: string | null;
  totalAmount: number;
  isoCurrencyCode: string;
  generatedAt: string;
  generatedByUserId: string | null;
  paidAt: string | null;
  distributorName: string | null;
  settlements: Array<{
    id: string;
    claimId: string;
    claimNumber: string;
    claimType: string;
    distributorName: string;
    amount: number;
    isoCurrencyCode: string;
    status: string;
    bankReference: string | null;
  }>;
}

interface CreditNotePreview {
  creditNoteRef: string;
  batchId: string;
  settlementType: string;
  nettingInstruction: string;
  currency: string;
  totalAmount: number;
  lines: Array<{
    claimNumber: string;
    distributorName: string;
    approvedAmount: number;
  }>;
}

const BANK_FORMAT_LABELS: Record<string, string> = {
  MANDIRI: 'Bank Mandiri Bulk Payment (CSV)',
  BCA: 'BCA KlikBCA Bisnis (CSV)',
  BNI: 'BNI Direct (CSV)',
  ISO20022_PAIN001: 'ISO 20022 PAIN.001 (XML)',
};

export function SettlementBatchPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [creditNote, setCreditNote] = useState<CreditNotePreview | null>(null);
  const [showCreditNote, setShowCreditNote] = useState(false);
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.get<BatchDetail>(`/settlement/batches/${id}`)
      .then(setBatch)
      .catch(() => setBatch(null))
      .finally(() => setLoading(false));
  }, [id]);

  async function downloadFile() {
    if (!id || !batch) return;
    try {
      const apiBase = import.meta.env['VITE_API_URL'] ?? '';
      const demoPersona = (() => {
        try { return JSON.parse(localStorage.getItem('demoPersona') ?? '{}') as { role?: string; userId?: string }; } catch { return {}; }
      })();
      const headers: Record<string, string> = { 'Accept-Language': 'id' };
      if (demoPersona.role && demoPersona.userId) {
        headers['x-demo-role'] = demoPersona.role;
        headers['x-demo-user-id'] = demoPersona.userId;
      }
      const res = await fetch(`${apiBase}/settlement/batches/${id}/file`, { headers });
      if (!res.ok) {
        setError(t('errors.internalServerError'));
        return;
      }
      const blob = await res.blob();
      const ext = batch.bankFormat === 'ISO20022_PAIN001' ? 'xml' : 'csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlement-${id.substring(0, 8)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t('errors.internalServerError'));
    }
  }

  async function loadCreditNote() {
    if (!id) return;
    try {
      const cn = await api.get<CreditNotePreview>(`/settlement/batches/${id}/credit-note-preview`);
      setCreditNote(cn);
      setShowCreditNote(true);
    } catch {
      setError(t('errors.internalServerError'));
    }
  }

  async function handleMarkPaid() {
    if (!id) return;
    setMarkingPaid(true);
    setError(null);
    try {
      await api.post(`/settlement/batches/${id}/mark-paid`, {});
      const updated = await api.get<BatchDetail>(`/settlement/batches/${id}`);
      setBatch(updated);
      setConfirmPaid(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.internalServerError'));
    } finally {
      setMarkingPaid(false);
    }
  }

  if (loading) return <p className="text-slate-400 text-sm py-8">{t('common.loading')}</p>;
  if (!batch) return <p className="text-danger text-sm py-8">{t('errors.notFound')}</p>;

  const isPaid = batch.status === 'PAID';
  const isBankTransfer = batch.settlementMethod === 'BANK_TRANSFER';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate('/approver/settlement')}
        className="text-xs text-slate-500 hover:text-copper transition-colors"
      >
        ← {t('settlement.queue.title')}
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="inline-block text-[11px] font-semibold tracking-[0.22em] uppercase text-copper mb-1">
            {t('settlement.batch.eyebrow')}
          </span>
          <h1 className="font-serif italic text-3xl text-ink font-mono text-lg">
            {batch.id.substring(0, 8).toUpperCase()}
          </h1>
          {batch.distributorName && (
            <p className="text-sm text-slate-500 mt-1">{batch.distributorName}</p>
          )}
        </div>
        <span className={['text-sm font-semibold px-3 py-1 rounded-sm', isPaid ? 'bg-success/10 text-success' : 'bg-copper-soft text-copper-deep'].join(' ')}>
          {t(`settlement.batchStatus.${batch.status}`, { defaultValue: batch.status })}
        </span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: t('settlement.batch.totalAmount'), value: formatCurrency(batch.totalAmount, batch.isoCurrencyCode) },
          { label: t('settlement.batch.method'), value: isBankTransfer ? (BANK_FORMAT_LABELS[batch.bankFormat ?? ''] ?? 'Bank Transfer') : t('settlement.picker.methods.INVOICE_NETTING.label') },
          { label: t('settlement.batch.generatedAt'), value: formatDate(batch.generatedAt) },
          { label: t('settlement.batch.claimCount'), value: String(batch.settlements.length) },
        ].map((item) => (
          <div key={item.label} className="p-3 border border-hairline rounded bg-bone">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{item.label}</p>
            <p className="font-mono text-sm font-medium text-ink break-words">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap">
        {isBankTransfer && (
          <button
            type="button"
            onClick={downloadFile}
            className="px-4 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
          >
            {t('settlement.batch.downloadFile')} ↓
          </button>
        )}
        {!isBankTransfer && (
          <button
            type="button"
            onClick={loadCreditNote}
            className="px-4 py-2 bg-copper text-bone-soft rounded text-sm font-semibold hover:bg-copper-warm transition-colors"
          >
            {t('settlement.batch.previewCreditNote')}
          </button>
        )}
        {!isPaid && (
          <button
            type="button"
            onClick={() => setConfirmPaid(true)}
            className="px-4 py-2 border border-success text-success rounded text-sm font-semibold hover:bg-success/5 transition-colors"
          >
            {t('settlement.batch.markPaid')}
          </button>
        )}
        {isPaid && batch.paidAt && (
          <p className="text-sm text-success font-medium py-2">
            ✓ {t('settlement.batch.paidOn', { date: formatDate(batch.paidAt) })}
          </p>
        )}
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      {/* Claims table */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">{t('settlement.batch.lines')}</p>
        <div className="border border-hairline rounded overflow-hidden p-4 bg-bone">
          <SettlementPreviewTable lines={batch.settlements} />
        </div>
      </div>

      {/* Mark paid confirm modal */}
      {confirmPaid && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4">
          <div className="bg-bone-soft border border-hairline rounded w-full max-w-sm shadow-xl p-6 space-y-4">
            <h2 className="font-serif italic text-xl text-ink">{t('settlement.batch.confirmPaidTitle')}</h2>
            <p className="text-sm text-slate-600">{t('settlement.batch.confirmPaidBody', { count: batch.settlements.length, amount: formatCurrency(batch.totalAmount, batch.isoCurrencyCode) })}</p>
            {error && <p className="text-sm text-danger">{error}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setConfirmPaid(false)} className="px-4 py-2 text-sm text-slate-600 hover:text-ink" disabled={markingPaid}>
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="px-5 py-2 bg-success text-white rounded text-sm font-semibold hover:bg-success/90 transition-colors disabled:opacity-50"
              >
                {markingPaid ? t('common.processing') : t('settlement.batch.confirmPaidConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit note slide-over */}
      {showCreditNote && creditNote && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex justify-end">
          <div className="bg-bone-soft w-full max-w-lg h-full overflow-y-auto shadow-xl border-l border-hairline">
            <div className="p-6 border-b border-hairline flex items-center justify-between">
              <h2 className="font-serif italic text-xl text-ink">{t('settlement.creditNote.title')}</h2>
              <button type="button" onClick={() => setShowCreditNote(false)} className="text-slate-400 hover:text-ink text-lg leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{t('settlement.creditNote.ref')}</p>
                  <p className="font-mono text-xs mt-0.5">{creditNote.creditNoteRef}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{t('settlement.creditNote.total')}</p>
                  <p className="font-mono text-sm font-medium mt-0.5">{formatCurrency(creditNote.totalAmount, creditNote.currency)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{t('settlement.creditNote.instruction')}</p>
                  <p className="text-xs mt-0.5">{t('settlement.creditNote.oldestInvoice')}</p>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{t('settlement.creditNote.lines')}</p>
                <div className="space-y-1">
                  {creditNote.lines.map((line, i) => (
                    <div key={i} className="flex justify-between text-xs py-1.5 border-b border-hairline last:border-0">
                      <span className="font-mono text-copper">{line.claimNumber}</span>
                      <span className="text-slate-500">{line.distributorName}</span>
                      <span className="font-mono">{formatCurrency(line.approvedAmount, creditNote.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-copper/5 border border-copper/20 rounded text-xs text-copper-deep">
                {t('settlement.creditNote.disclaimer')}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
